/**
 * /backgammon — the board: an SVG drawn from a position, and the two ways a
 * person moves a checker on it — tap a checker, then a highlighted point; or
 * drag it there.
 *
 * The board is drawn from ONE player's side (`viewer`), and both games use the
 * one diagram every source draws: the viewer's point 24 is the far right
 * corner, he moves along the top right→left and the bottom left→right —
 * anticlockwise — and his home (points 1–6) is the near right quarter, next to
 * the tray. Short game: the usual backgammon diagram. Long game: his head is
 * that far right corner, the opponent's head the near left one, and both go
 * round anticlockwise (bkgm.com «Narde»: «the rightmost point of the far side
 * … counterclockwise … White bears off at the lower-right»; the setup picture
 * of ru.wikipedia «Длинные нарды»). Until 19.10.2026 the long board was this
 * picture flipped top to bottom (head near right, clockwise) — read off the
 * words of [MS 20.2] without the picture; a player who learned from a book
 * called it wrong. The test tests/js/backgammon-board-diagram.test.mjs holds it.
 * `mirror` swaps left and right (home and tray on the left).
 *
 * Everything the controller passes and receives is in the MOVER's own point
 * numbers (st.mover), so the controller never converts coordinates; the board
 * maps them to the screen through the absolute index.
 *
 * Layers. The frame, the points and the numbers change only with the mirror
 * and the numbers switch — they are built once per such
 * change (`staticKey`), not on every render: a render during a drag or a
 * checker flight touches the checkers, the marks and the dice only.
 *
 * Colours are CSS custom properties of the page (board themes live in the
 * blade): the SVG carries classes and gradient stops, never a colour literal,
 * so a theme switch is a class on the app and costs no redraw at all.
 */
import * as R from './rules.js';

const NS = 'http://www.w3.org/2000/svg';

/* geometry in SVG units */
const PT = 60;                 // point width
const CR = 27;                 // checker radius
const BARW = 48;
const HALF = 6 * PT;           // 360
const TRAY = 58;
const MARGIN = 16;
const TOP = 42;                // top of the upper points
const PLEN = 290;              // point length
const MID0 = TOP + PLEN;       // 332
const MID1 = MID0 + 66;        // 398 — the band between the rows (dice, cube)
const BOT = MID1 + PLEN;       // 688 — bottom edge of the lower points
const H = BOT + 42;            // 730
const W = MARGIN + HALF + BARW + HALF + MARGIN + TRAY + 10;   // 868

export const BOARD_RATIO = W / H;

function el(tag, attrs, parent) {
    const e = document.createElementNS(NS, tag);
    if (attrs) for (const k in attrs) e.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(e);
    return e;
}

/** Screen slot of the viewer's own point q: row 'T'|'B', column 0..11 left→right.
    The same in both games (see the header). */
export function slotOf(q, mirror) {
    let row, i;
    if (q <= 12) { row = 'B'; i = 12 - q; }               // 1 → 11 (near right) … 12 → 0 (near left)
    else { row = 'T'; i = q - 13; }                        // 13 → 0 (far left) … 24 → 11 (far right)
    if (mirror) i = 11 - i;
    return { row, i };
}

/** A point's triangle at column centre x: the base on the board's edge, the tip
    towards the middle band — in both rows, as on a real board. The point and
    its «you may go here» outline are both drawn from this one shape. */
export function pointTriangle(x, top) {
    const x0 = x - PT / 2 + 3, x1 = x + PT / 2 - 3;
    const base = top ? TOP : BOT;
    const tip = top ? TOP + PLEN - 20 : BOT - PLEN + 20;
    return { d: `M${x0},${base} L${x1},${base} L${x},${tip} Z`, base, tip };
}

/** The drawn point: its triangle as it is (no transform — until 19.10.2026 the
    lower row was turned 180° on top of an already upward triangle, and both
    rows pointed down), shaded from the base to the tip in both rows: the lower
    row takes the upward copy of the gradient (`…u`). */
export function pointAttrs(x, top, dark) {
    return {
        d: pointTriangle(x, top).d,
        class: 'bg-pt ' + (dark ? 'bg-pt-a' : 'bg-pt-b'),
        fill: `url(#bgPt${dark ? 'A' : 'B'}${top ? '' : 'u'})`,
    };
}

export function createBoard(svg, opts) {
    opts = opts || {};
    const st = {
        pos: null, viewer: 0, mover: 0, mirror: false, numbers: true,
        sources: new Set(), dests: new Map(), selected: null,
        last: null, lastBy: 0, dice: null, diceOwner: 0, usedDice: [], cube: null,
        busy: false, hint: null, rolling: false,
    };
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    const defs = el('defs', null, svg);
    const radial = (id, a, b, c) => {
        const g = el('radialGradient', { id, cx: '36%', cy: '30%', r: '78%' }, defs);
        el('stop', { offset: '0%', class: a }, g);
        el('stop', { offset: '55%', class: b }, g);
        el('stop', { offset: '100%', class: c }, g);
    };
    radial('bgChkW', 'bg-s-w1', 'bg-s-w2', 'bg-s-w3');
    radial('bgChkB', 'bg-s-b1', 'bg-s-b2', 'bg-s-b3');
    const lin = (id, a, b, dir) => {
        const xy = dir === 'down' ? { x1: 0, y1: 0, x2: 0, y2: 1 } : dir === 'up' ? { x1: 0, y1: 1, x2: 0, y2: 0 } : { x1: 0, y1: 0, x2: 1, y2: 1 };
        const g = el('linearGradient', Object.assign({ id }, xy), defs);
        el('stop', { offset: '0%', class: a }, g);
        el('stop', { offset: '100%', class: b }, g);
    };
    lin('bgFrame', 'bg-s-f1', 'bg-s-f2');
    lin('bgField', 'bg-s-fd1', 'bg-s-fd2', 'down');
    // points: base → tip; the lower row's tips point up, so its copy runs upwards
    lin('bgPtA', 'bg-s-pa1', 'bg-s-pa2', 'down');
    lin('bgPtB', 'bg-s-pb1', 'bg-s-pb2', 'down');
    lin('bgPtAu', 'bg-s-pa1', 'bg-s-pa2', 'up');
    lin('bgPtBu', 'bg-s-pb1', 'bg-s-pb2', 'up');
    lin('bgDieW', 'bg-s-dw1', 'bg-s-dw2');
    lin('bgDieB', 'bg-s-db1', 'bg-s-db2');
    // the grain of a wooden frame: a tiled pattern of thin lines, drawn once
    const grain = el('pattern', { id: 'bgGrain', width: 220, height: 36, patternUnits: 'userSpaceOnUse' }, defs);
    for (const [y, a] of [[4, 0.5], [11, 0.25], [17, 0.6], [24, 0.3], [31, 0.45]]) {
        el('path', { d: `M0 ${y} C 55 ${y - 3}, 110 ${y + 3}, 165 ${y - 1} S 220 ${y + 1}, 220 ${y}`, class: 'bg-grain-line', 'stroke-opacity': a }, grain);
    }
    const arrow = el('marker', { id: 'bgArrow', viewBox: '0 0 10 10', refX: 6, refY: 5, markerWidth: 3.6, markerHeight: 3.6, orient: 'auto-start-reverse' }, defs);
    el('path', { d: 'M0 0 L10 5 L0 10 z', class: 'bg-arrowhead' }, arrow);
    const arrowH = el('marker', { id: 'bgArrowHint', viewBox: '0 0 10 10', refX: 6, refY: 5, markerWidth: 3.6, markerHeight: 3.6, orient: 'auto-start-reverse' }, defs);
    el('path', { d: 'M0 0 L10 5 L0 10 z', class: 'bg-arrowhead-hint' }, arrowH);

    const gStatic = el('g', { class: 'bg-static' }, svg);
    const gMarks = el('g', { class: 'bg-marks' }, svg);
    const gCheckers = el('g', { class: 'bg-checkers' }, svg);
    const gArrows = el('g', { class: 'bg-arrows' }, svg);
    const gDice = el('g', { class: 'bg-dice' }, svg);
    const gTop = el('g', { class: 'bg-overlay' }, svg);
    let staticKey = '';

    /* x of the field and the tray: the tray sits on the home side (right),
       or on the left when mirrored */
    const fieldX = () => (st.mirror ? 10 + TRAY + MARGIN : MARGIN);
    const trayX = () => (st.mirror ? 10 : MARGIN + HALF + BARW + HALF + MARGIN);
    const barX = () => fieldX() + HALF;
    const colX = (i) => fieldX() + (i < 6 ? i * PT : HALF + BARW + (i - 6) * PT) + PT / 2;

    /* mover-relative ↔ viewer-relative */
    const v = () => st.pos.v;
    const toViewer = (p, q) => (p === st.viewer ? q : R.ABS2REL[v()][st.viewer][R.REL2ABS[v()][p][q]]);
    const fromViewer = (p, vq) => (p === st.viewer ? vq : R.ABS2REL[v()][p][R.REL2ABS[v()][st.viewer][vq]]);

    /* where side p bears off: the viewer's home is the near quarter by the tray,
       so he bears off into the lower tray half, the other side into the upper */
    function trayTop(p) {
        return p !== st.viewer;
    }
    function trayRect(top) {
        const h = (BOT - TOP) / 2 - 6;
        return { x: trayX(), y: top ? TOP : BOT - h, w: TRAY, h };
    }

    function geomV(vq) {
        const s = slotOf(vq, st.mirror);
        return { x: colX(s.i), top: s.row === 'T', s };
    }

    function stackY(top, k, count) {
        const maxLen = PLEN - 4;
        const step = count <= 5 ? 2 * CR + 1 : Math.max(10, (maxLen - 2 * CR) / (count - 1));
        return top ? TOP + CR + 1 + k * step : BOT - CR - 1 - k * step;
    }

    function checkerXY(p, q, k, count) {
        if (q === R.BAR) {
            // a hit checker waits on the bar in the half it enters towards (the
            // short game only — nothing is hit in the long one): the viewer
            // enters the opponent's home, the far quarter
            const upper = p === st.viewer;
            const y = upper ? MID0 - CR - 4 - k * (2 * CR - 6) : MID1 + CR + 4 + k * (2 * CR - 6);
            return { x: barX() + BARW / 2, y };
        }
        if (q === R.OFF) {
            const top = trayTop(p);
            const r = trayRect(top);
            const y = top ? r.y + 12 + k * 17 : r.y + r.h - 12 - k * 17;
            return { x: r.x + TRAY / 2, y, top };
        }
        const g = geomV(toViewer(p, q));
        return { x: g.x, y: stackY(g.top, k, count) };
    }

    /* the point a step of a checker would land on, as it is drawn now */
    function landingXY(p, q) {
        if (q === R.OFF) { const r = trayRect(trayTop(p)); return { x: r.x + TRAY / 2, y: r.y + r.h / 2 }; }
        const n = q === R.BAR ? st.pos.bar[p] : R.countAt(st.pos, p, q);
        return checkerXY(p, q, Math.max(0, n - 1), Math.max(1, n));
    }

    function checker(x, y, p, extra, parent) {
        const g = el('g', { class: 'bg-chk ' + (p === 0 ? 'bg-chk-w' : 'bg-chk-b') + (extra ? ' ' + extra : ''), transform: `translate(${x},${y})` }, parent || gCheckers);
        el('circle', { r: CR, cx: 1.5, cy: 3, class: 'bg-chk-shadow' }, g);
        el('circle', { r: CR, class: 'bg-chk-body', fill: p === 0 ? 'url(#bgChkW)' : 'url(#bgChkB)' }, g);
        el('circle', { r: CR - 7.5, class: 'bg-chk-ring' }, g);
        el('circle', { r: CR - 14, class: 'bg-chk-core' }, g);
        return g;
    }

    /* ─────────────── the static layer ─────────────── */

    function drawStatic() {
        const key = [st.mirror ? 1 : 0, st.numbers ? 1 : 0].join('|');
        if (key === staticKey) return;
        staticKey = key;
        gStatic.textContent = '';
        el('rect', { x: 0, y: 0, width: W, height: H, rx: 22, class: 'bg-frame', fill: 'url(#bgFrame)' }, gStatic);
        el('rect', { x: 0, y: 0, width: W, height: H, rx: 22, class: 'bg-frame-grain', fill: 'url(#bgGrain)' }, gStatic);
        el('rect', { x: 3, y: 3, width: W - 6, height: H - 6, rx: 20, class: 'bg-frame-edge' }, gStatic);
        const fx = fieldX();
        for (const x0 of [fx, fx + HALF + BARW]) {
            el('rect', { x: x0 - 5, y: TOP - 7, width: HALF + 10, height: BOT - TOP + 14, rx: 9, class: 'bg-field', fill: 'url(#bgField)' }, gStatic);
            el('rect', { x: x0 - 5, y: TOP - 7, width: HALF + 10, height: BOT - TOP + 14, rx: 9, class: 'bg-field-inset' }, gStatic);
        }
        el('rect', { x: barX() + 7, y: TOP - 7, width: BARW - 14, height: BOT - TOP + 14, rx: 7, class: 'bg-barstrip' }, gStatic);
        for (const top of [true, false]) {
            const r = trayRect(top);
            el('rect', { x: r.x, y: r.y, width: r.w, height: r.h, rx: 9, class: 'bg-tray' }, gStatic);
        }
        for (let vq = 1; vq <= 24; vq++) {
            const g = geomV(vq);
            const dark = (g.s.i + (g.top ? 0 : 1)) % 2 === 0;
            el('path', pointAttrs(g.x, g.top, dark), gStatic);
            if (st.numbers) {
                // numbers are the VIEWER's own count — the notation in the move list uses the same
                const t = el('text', { x: g.x, y: g.top ? TOP - 13 : BOT + 32, class: 'bg-num', 'text-anchor': 'middle' }, gStatic);
                t.textContent = String(vq);
            }
        }
    }

    /* ─────────────── checkers ─────────────── */

    function drawCheckers() {
        gCheckers.textContent = '';
        const pos = st.pos;
        for (let p = 0; p < 2; p++) {
            for (let q = 1; q <= 24; q++) {
                const n = R.countAt(pos, p, q);
                if (!n) continue;
                for (let k = 0; k < n; k++) {
                    const xy = checkerXY(p, q, k, n);
                    const topOne = k === n - 1;
                    const movable = topOne && p === st.mover && st.sources.has(q);
                    const g = checker(xy.x, xy.y, p, movable ? 'bg-chk-movable' : '');
                    if (topOne) {
                        g.setAttribute('data-top', p + ':' + q);
                        if (movable) el('circle', { r: CR + 4, class: 'bg-chk-halo' }, g).parentNode.insertBefore(g.lastChild, g.firstChild);
                        if (p === st.mover && st.selected === q) g.classList.add('bg-chk-sel');
                        if (n > 5) {
                            const t = el('text', { class: 'bg-chk-n', 'text-anchor': 'middle', y: 10 }, g);
                            t.textContent = String(n);
                        }
                    }
                }
            }
            const nb = pos.bar[p];
            const shown = Math.min(nb, 4);
            for (let k = 0; k < shown; k++) {
                const xy = checkerXY(p, R.BAR, k, nb);
                const topOne = k === shown - 1;
                const movable = topOne && p === st.mover && st.sources.has(R.BAR);
                const g = checker(xy.x, xy.y, p, movable ? 'bg-chk-movable' : '');
                if (topOne) {
                    g.setAttribute('data-top', p + ':' + R.BAR);
                    if (movable) el('circle', { r: CR + 4, class: 'bg-chk-halo' }, g).parentNode.insertBefore(g.lastChild, g.firstChild);
                    if (p === st.mover && st.selected === R.BAR) g.classList.add('bg-chk-sel');
                    if (nb > 1) {
                        const t = el('text', { class: 'bg-chk-n', 'text-anchor': 'middle', y: 10 }, g);
                        t.textContent = String(nb);
                    }
                }
            }
            const no = pos.off[p];
            for (let k = 0; k < no; k++) {
                const xy = checkerXY(p, R.OFF, k, no);
                el('rect', { x: xy.x - TRAY / 2 + 7, y: xy.y - 7, width: TRAY - 14, height: 14, rx: 5, class: 'bg-offchk ' + (p === 0 ? 'bg-chk-w' : 'bg-chk-b'), fill: p === 0 ? 'url(#bgChkW)' : 'url(#bgChkB)' }, gCheckers);
            }
            if (no) {
                const r = trayRect(trayTop(p));
                const t = el('text', { x: r.x + TRAY / 2, y: trayTop(p) ? r.y + r.h - 12 : r.y + 34, class: 'bg-off-n', 'text-anchor': 'middle' }, gCheckers);
                t.textContent = String(no);
            }
        }
    }

    /* ─────────────── marks: where a checker may go, where it may start ─────────────── */

    function drawMarks() {
        gMarks.textContent = '';
        for (const [to] of st.dests) {
            if (to === R.OFF) {
                const r = trayRect(trayTop(st.mover));
                el('rect', { x: r.x + 2, y: r.y + 2, width: r.w - 4, height: r.h - 4, rx: 9, class: 'bg-dest-tray' }, gMarks);
                continue;
            }
            const vq = toViewer(st.mover, to);
            const g = geomV(vq);
            el('path', { d: pointTriangle(g.x, g.top).d, class: 'bg-pt-dest' }, gMarks);
        }
        if (st.selected == null) {
            for (const q of st.sources) {
                if (q === R.BAR) continue;
                const g = geomV(toViewer(st.mover, q));
                el('rect', { x: g.x - PT / 2 + 8, y: g.top ? TOP - 5 : BOT - 1, width: PT - 16, height: 6, rx: 3, class: 'bg-srcmark' }, gMarks);
            }
        }
    }

    /* ghost checkers where the selected one may land — drawn over the stacks */
    function drawGhosts() {
        for (const [to, d] of st.dests) {
            if (to === R.OFF) {
                const r = trayRect(trayTop(st.mover));
                const t = el('text', { x: r.x + TRAY / 2, y: r.y + r.h / 2 + 8, class: 'bg-dest-off', 'text-anchor': 'middle' }, gCheckers);
                t.textContent = '↧';
                continue;
            }
            const n = R.countAt(st.pos, st.mover, to);
            const xy = checkerXY(st.mover, to, Math.min(n, n > 4 ? n : n), n + 1);
            const g = checker(xy.x, xy.y, st.mover, 'bg-chk-ghost');
            el('circle', { r: 6, class: 'bg-ghost-dot' }, g);
            if (d && d.steps && d.steps.length > 1) {
                g.lastChild.remove();
                const t = el('text', { class: 'bg-ghost-n', 'text-anchor': 'middle', y: 9 }, g);
                t.textContent = '+' + d.steps.length;
            }
        }
    }

    /* ─────────────── arrows: the last turn, the hint ─────────────── */

    function chains(moves) {
        const out = [];
        const used = new Array(moves.length).fill(false);
        for (let i = 0; i < moves.length; i++) {
            if (used[i]) continue;
            used[i] = true;
            const ch = [moves[i]];
            let end = moves[i].to;
            for (let j = i + 1; j < moves.length && end !== R.OFF; j++) {
                if (!used[j] && moves[j].from === end) { used[j] = true; ch.push(moves[j]); end = moves[j].to; }
            }
            out.push(ch);
        }
        return out;
    }

    function arrowPath(a, b) {
        const dx = b.x - a.x, dy = b.y - a.y;
        const len = Math.hypot(dx, dy) || 1;
        // a bow away from the centre band, so arrows along one row do not cover the points
        const bow = Math.min(90, len * 0.28);
        const nx = -dy / len, ny = dx / len;
        const sideY = (a.y + b.y) / 2 < (MID0 + MID1) / 2 ? 1 : -1;
        const s = Math.sign(ny * sideY) || 1;
        const cx = (a.x + b.x) / 2 + nx * bow * s, cy = (a.y + b.y) / 2 + ny * bow * s;
        // stop short of the target checker so the head sits on its rim
        const ex = b.x - (b.x - cx) / Math.hypot(b.x - cx, b.y - cy) * (CR * 0.7);
        const ey = b.y - (b.y - cy) / Math.hypot(b.x - cx, b.y - cy) * (CR * 0.7);
        return `M${a.x},${a.y} Q${cx},${cy} ${ex},${ey}`;
    }

    function drawArrows() {
        gArrows.textContent = '';
        const draw = (moves, p, cls, marker, before) => {
            // `before`: the position the moves start from (the hint), else the position after them
            const counts = {};
            const key = (q) => p + ':' + q;
            for (const ch of chains(moves)) {
                const from = ch[0].from, to = ch[ch.length - 1].to;
                let a, b;
                if (before) {
                    const n = from === R.BAR ? st.pos.bar[p] : R.countAt(st.pos, p, from);
                    const used = counts[key(from)] || 0;
                    counts[key(from)] = used + 1;
                    a = checkerXY(p, from, Math.max(0, n - 1 - used), Math.max(1, n));
                    b = to === R.OFF ? landingXY(p, R.OFF) : checkerXY(p, to, R.countAt(st.pos, p, to) + (counts[key(to) + '>'] = (counts[key(to) + '>'] || 0) + 1) - 1, R.countAt(st.pos, p, to) + 1);
                } else {
                    const n = from === R.BAR ? st.pos.bar[p] : R.countAt(st.pos, p, from);
                    const used = counts[key(from)] || 0;
                    counts[key(from)] = used + 1;
                    a = from === R.BAR ? checkerXY(p, R.BAR, 0, 1) : checkerXY(p, from, n + used, n + used + 1);
                    b = landingXY(p, to);
                }
                el('path', { d: arrowPath(a, b), class: cls, 'marker-end': `url(#${marker})` }, gArrows);
                for (const m of ch) if (m.hit) {
                    const hx = landingXY(p, m.to);
                    el('circle', { cx: hx.x, cy: hx.y, r: CR + 6, class: 'bg-hitmark' }, gArrows);
                }
            }
        };
        if (st.last && st.last.length && st.showLast !== false) draw(st.last, st.lastBy, 'bg-lastarrow', 'bgArrow', false);
        // a move under review: the position BEFORE it, the played move and the better one
        if (st.played && st.played.length) draw(st.played, st.playedBy, 'bg-lastarrow', 'bgArrow', true);
        if (st.hint && st.hint.length) draw(st.hint, st.hintBy != null ? st.hintBy : st.mover, 'bg-hintarrow', 'bgArrowHint', true);
    }

    /* ─────────────── dice and the cube ─────────────── */

    let diceBox = null;
    function drawDice() {
        gDice.textContent = '';
        diceBox = null;
        if (!st.dice) return;
        const [a, b] = st.dice;
        const vals = a === b ? [a, a, a, a] : [a, b];
        const usedLeft = (st.usedDice || []).slice();
        const size = a === b ? 44 : 52;
        const gap = a === b ? 8 : 12;
        const total = vals.length * size + (vals.length - 1) * gap;
        // the viewer rolls on the right half, the other side on the left (as at a real board)
        const right = (st.diceOwner === st.viewer) !== st.mirror;
        const cx = right ? fieldX() + HALF + BARW + HALF / 2 : fieldX() + HALF / 2;
        const cy = (MID0 + MID1) / 2;
        let x = cx - total / 2;
        diceBox = { x0: x - 14, x1: x + total + 14, y0: cy - size / 2 - 14, y1: cy + size / 2 + 14 };
        vals.forEach((d, i) => {
            const k = usedLeft.indexOf(d);
            if (k >= 0) usedLeft.splice(k, 1);
            const tilt = st.rolling ? ((i % 2 ? 1 : -1) * (6 + ((d * 7) % 11))) : (i % 2 ? 2 : -2);
            const g = el('g', {
                class: 'bg-die' + (k >= 0 ? ' bg-die-used' : '') + (st.diceOwner === 0 ? ' bg-die-w' : ' bg-die-b') + (st.rolling ? ' bg-die-rolling' : ''),
                transform: `translate(${x + size / 2},${cy}) rotate(${tilt}) translate(${-size / 2},${-size / 2})`,
            }, gDice);
            el('rect', { x: 2, y: 4, width: size, height: size, rx: size * 0.2, class: 'bg-die-shadow' }, g);
            el('rect', { width: size, height: size, rx: size * 0.2, class: 'bg-die-body', fill: st.diceOwner === 0 ? 'url(#bgDieW)' : 'url(#bgDieB)' }, g);
            for (const [px, py] of PIPS[d]) el('circle', { cx: px * size, cy: py * size, r: size * 0.088, class: 'bg-die-pip' }, g);
            x += size + gap;
        });
    }

    /* the win chance of the viewer as a thin gauge along the middle bar:
       his colour fills it from his side (the bottom), the other side's from the top */
    function drawMeter() {
        if (st.meter == null) return;
        const x = barX() + BARW / 2 - 4, y0 = TOP - 2, h = BOT - TOP + 4;
        const f = Math.max(0.02, Math.min(0.98, st.meter));
        el('rect', { x, y: y0, width: 8, height: h, rx: 4, class: 'bg-meter-bg' }, gTop);
        el('rect', { x, y: y0 + h * (1 - f), width: 8, height: h * f, rx: 4, class: 'bg-meter-v ' + (st.viewer === 0 ? 'bg-meter-w' : 'bg-meter-b') }, gTop);
        el('rect', { x: x - 3, y: y0 + h / 2 - 1, width: 14, height: 2, class: 'bg-meter-mid' }, gTop);
    }

    function drawCube() {
        gTop.textContent = '';
        drawMeter();
        if (!st.cube) return;
        const size = 42;
        const x = barX() + BARW / 2 - size / 2;
        let y = (MID0 + MID1) / 2 - size / 2;
        if (st.cube.owner === st.viewer) y = BOT - size - 8;
        else if (st.cube.owner >= 0) y = TOP + 8;
        const g = el('g', { class: 'bg-cube', transform: `translate(${x},${y})` }, gTop);
        el('rect', { x: 2, y: 3, width: size, height: size, rx: 8, class: 'bg-die-shadow' }, g);
        el('rect', { width: size, height: size, rx: 8, class: 'bg-cube-body' }, g);
        const t = el('text', { x: size / 2, y: size / 2 + 9, 'text-anchor': 'middle', class: 'bg-cube-n' }, g);
        t.textContent = String(st.cube.value === 1 ? 64 : st.cube.value);
    }

    /*
     * Each layer is rebuilt only when what it shows changed. A render comes on
     * every state change — and 14 times a second while the dice tumble, when
     * only the dice change — and rebuilding ~150 checker elements each time
     * was the bulk of the page's work per click (INP). `force` rebuilds all:
     * after a drag or a flight moved elements by hand.
     */
    const keys = { marks: '', checkers: '', arrows: '', dice: '', top: '' };
    const setKey = (set) => [...set].join(',');
    const mapKey = (m) => [...m.keys()].join(',');
    function render(force) {
        if (!st.pos) return;
        if (force) for (const k in keys) keys[k] = '';
        drawStatic();
        const base = st.viewer + '|' + (st.mirror ? 1 : 0) + '|' + st.mover + '|' + R.positionKey(st.pos);
        const sel = setKey(st.sources) + '|' + st.selected + '|' + mapKey(st.dests);
        let k = base + '|' + sel;
        if (k !== keys.marks) { keys.marks = k; drawMarks(); }
        if (k !== keys.checkers) {
            keys.checkers = k;
            drawCheckers();
            if (st.selected != null && st.dests.size) drawGhosts();
        }
        k = base + '|' + JSON.stringify([st.last, st.lastBy, st.showLast, st.played, st.playedBy, st.hint, st.hintBy]);
        if (k !== keys.arrows) { keys.arrows = k; drawArrows(); }
        k = st.viewer + '|' + (st.mirror ? 1 : 0) + '|' + JSON.stringify([st.dice, st.usedDice, st.diceOwner, !!st.rolling]);
        if (k !== keys.dice) { keys.dice = k; drawDice(); }
        k = st.viewer + '|' + (st.mirror ? 1 : 0) + '|' + JSON.stringify([st.cube, st.meter]);
        if (k !== keys.top) { keys.top = k; drawCube(); }
        if (kq != null) focusRing(kq);
    }

    /* ─────────────── input ─────────────── */

    function toSvg(clientX, clientY) {
        const m = svg.getScreenCTM();
        if (!m) return null;
        const pt = svg.createSVGPoint();
        pt.x = clientX; pt.y = clientY;
        return pt.matrixTransform(m.inverse());
    }

    /** The mover's point under a screen coordinate (BAR, OFF or 1..24), or null. */
    function qAt(clientX, clientY) {
        if (!st.pos) return null;
        const p = toSvg(clientX, clientY);
        if (!p) return null;
        const tx = trayX();
        if (p.x >= tx - 8 && p.x <= tx + TRAY + 8 && p.y >= TOP - 20 && p.y <= BOT + 20) return R.OFF;
        const bx = barX();
        if (p.x >= bx && p.x <= bx + BARW) return R.BAR;
        const fx = fieldX();
        let i = -1;
        if (p.x >= fx && p.x < fx + HALF) i = Math.floor((p.x - fx) / PT);
        else if (p.x >= fx + HALF + BARW && p.x < fx + 2 * HALF + BARW) i = 6 + Math.floor((p.x - fx - HALF - BARW) / PT);
        if (i < 0) return null;
        const row = p.y < (MID0 + MID1) / 2 ? 'T' : 'B';
        for (let vq = 1; vq <= 24; vq++) {
            const s = slotOf(vq, st.mirror);
            if (s.row === row && s.i === i) return fromViewer(st.mover, vq);
        }
        return null;
    }

    /**
     * A finger is wider than a point on a phone (≈24 px there). When a checker
     * is selected and the tap lands next to a highlighted point rather than on
     * it, take the nearest highlighted point within one column on that side of
     * the board.
     */
    function snapDest(clientX, clientY) {
        if (!st.dests.size) return null;
        const p = toSvg(clientX, clientY);
        if (!p) return null;
        const topHalf = p.y < (MID0 + MID1) / 2;
        let best = null, bd = Infinity;
        for (const [to] of st.dests) {
            if (to === R.OFF) continue;
            const g = geomV(toViewer(st.mover, to));
            if (g.top !== topHalf) continue;
            const d = Math.abs(g.x - p.x);
            if (d <= PT * 0.95 && d < bd) { bd = d; best = to; }
        }
        return best;
    }

    function tapQ(q) {
        if (st.busy) return;
        if (st.selected != null && q != null && st.dests.has(q)) { opts.onMove && opts.onMove(st.selected, q); return; }
        if (q != null && st.sources.has(q)) {
            opts.onSelect && opts.onSelect(st.selected === q ? null : q);
            return;
        }
        if (st.selected != null) { opts.onIllegal && opts.onIllegal(st.selected, q); return; }
        opts.onBoardTap && opts.onBoardTap(q);
    }

    let drag = null;
    svg.addEventListener('pointerdown', (e) => {
        if (st.busy || !st.pos) return;
        if (e.button != null && e.button > 0) return;
        const q = qAt(e.clientX, e.clientY);
        drag = { q, x: e.clientX, y: e.clientY, moved: false, id: e.pointerId, ghost: null, can: q != null && st.sources.has(q) };
        if (drag.can) { try { svg.setPointerCapture(e.pointerId); } catch (_) {} }
    });
    svg.addEventListener('pointermove', (e) => {
        if (!drag || !drag.can || drag.id !== e.pointerId) return;
        const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
        if (!drag.moved && dx * dx + dy * dy < 81) return;
        if (!drag.moved) {
            drag.moved = true;
            if (st.selected !== drag.q && opts.onSelect) opts.onSelect(drag.q);
            drag.ghost = gCheckers.querySelector(`[data-top="${st.mover}:${drag.q}"]`);
            if (drag.ghost) { drag.ghost.classList.add('bg-chk-lift'); keys.checkers = ''; }
        }
        if (drag.ghost) {
            const p = toSvg(e.clientX, e.clientY);
            if (p) {
                drag.ghost.setAttribute('transform', `translate(${p.x},${p.y})`);
                gCheckers.appendChild(drag.ghost);
            }
        }
        e.preventDefault();
    });
    svg.addEventListener('pointerup', (e) => {
        if (!drag || drag.id !== e.pointerId) return;
        const d = drag;
        drag = null;
        let q = qAt(e.clientX, e.clientY);
        if (st.selected != null && (q == null || (!st.dests.has(q) && !st.sources.has(q)))) {
            const near = snapDest(e.clientX, e.clientY);
            if (near != null) q = near;
        }
        if (d.moved) {
            if (q != null && st.dests.has(q) && st.selected != null && opts.onMove) opts.onMove(st.selected, q);
            else render(true);
            return;
        }
        // the dice are a button of their own: a tap on them throws or finishes the turn
        if (st.selected == null && diceBox && opts.onDiceTap && !st.busy) {
            const p = toSvg(e.clientX, e.clientY);
            if (p && p.x >= diceBox.x0 && p.x <= diceBox.x1 && p.y >= diceBox.y0 && p.y <= diceBox.y1) { opts.onDiceTap(); return; }
        }
        tapQ(q);
    });
    svg.addEventListener('pointercancel', () => { if (drag) { drag = null; render(true); } });
    // a double click on a checker plays it by the largest single step it has
    svg.addEventListener('dblclick', (e) => {
        if (st.busy || !st.pos || !opts.onAutoMove) return;
        const q = qAt(e.clientX, e.clientY);
        if (q != null && st.sources.has(q)) opts.onAutoMove(q);
    });

    /* keyboard: ←/→ walk the movable checkers (then the destinations), Enter picks */
    let kq = null;
    svg.setAttribute('tabindex', '0');
    svg.addEventListener('keydown', (e) => {
        if (!st.pos) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            const list = st.selected != null ? [...st.dests.keys()] : [...st.sources];
            if (!list.length) return;
            const i = list.indexOf(kq);
            const step = e.key === 'ArrowRight' ? 1 : list.length - 1;
            kq = list[i < 0 ? 0 : (i + step) % list.length];
            focusRing(kq);
            if (opts.onFocusPoint) opts.onFocusPoint(kq);
            e.preventDefault();
        } else if (e.key === 'Enter' || e.key === ' ') {
            const q = kq;
            if (q != null && st.selected != null && st.dests.has(q)) kq = null;
            tapQ(q);
            e.preventDefault();
        } else if (e.key === 'Escape') {
            kq = null;
            opts.onSelect && opts.onSelect(null);
        }
    });
    function focusRing(q) {
        gMarks.querySelectorAll('.bg-kfocus').forEach((n) => n.remove());
        if (q == null || !st.pos) return;
        if (q === R.OFF) {
            const r = trayRect(trayTop(st.mover));
            el('rect', { x: r.x, y: r.y, width: r.w, height: r.h, rx: 9, class: 'bg-kfocus' }, gMarks);
            return;
        }
        if (q === R.BAR) {
            el('rect', { x: barX(), y: TOP, width: BARW, height: BOT - TOP, rx: 8, class: 'bg-kfocus' }, gMarks);
            return;
        }
        const g = geomV(toViewer(st.mover, q));
        el('rect', { x: g.x - PT / 2 + 2, y: g.top ? TOP : MID1, width: PT - 4, height: PLEN, rx: 6, class: 'bg-kfocus' }, gMarks);
    }

    /* ─────────────── a checker flying from one point to another ─────────────── */
    function animateStep(p, from, to, ms) {
        return new Promise((resolve) => {
            if (!ms || !st.pos) { resolve(); return; }
            const pos = st.pos;
            const nFrom = from === R.BAR ? pos.bar[p] : R.countAt(pos, p, from);
            const nTo = to === R.OFF ? pos.off[p] : R.countAt(pos, p, to);
            const a = checkerXY(p, from, Math.max(0, Math.min(nFrom, from === R.BAR ? 4 : 99) - 1), nFrom);
            const b = checkerXY(p, to, nTo, nTo + 1);
            keys.checkers = '';
            // hide the checker that is leaving
            const src = gCheckers.querySelector(`[data-top="${p}:${from}"]`);
            if (src && nFrom === 1) src.style.opacity = '0';
            const g = checker(a.x, a.y, p, 'bg-chk-fly');
            const t0 = performance.now();
            const lift = Math.min(60, 18 + Math.hypot(b.x - a.x, b.y - a.y) * 0.08);
            const tick = (t) => {
                const k = Math.min(1, (t - t0) / ms);
                const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
                const x = a.x + (b.x - a.x) * e, y = a.y + (b.y - a.y) * e - Math.sin(Math.PI * k) * lift;
                g.setAttribute('transform', `translate(${x},${y})`);
                if (k < 1) requestAnimationFrame(tick);
                else { g.remove(); resolve(); }
            };
            requestAnimationFrame(tick);
        });
    }

    return {
        W, H,
        set(o) { Object.assign(st, o); render(); },
        get state() { return st; },
        render,
        animateStep,
        qAt,
        /** screen centre (client px) of the dice, or null — for tests */
        diceXY() {
            const m = svg.getScreenCTM();
            if (!m || !diceBox) return null;
            const pt = svg.createSVGPoint();
            pt.x = (diceBox.x0 + diceBox.x1) / 2; pt.y = (diceBox.y0 + diceBox.y1) / 2;
            const s = pt.matrixTransform(m);
            return { x: s.x, y: s.y };
        },
        /** screen centre (client px) of the mover's point q — for tests and focus */
        clientXY(p, q) {
            const m = svg.getScreenCTM();
            if (!m || !st.pos) return null;
            let x, y;
            if (q === R.OFF) { const r = trayRect(trayTop(p)); x = r.x + r.w / 2; y = r.y + r.h / 2; }
            else if (q === R.BAR) { x = barX() + BARW / 2; y = (MID0 + MID1) / 2 - 60; }
            else { const g = geomV(toViewer(p, q)); x = g.x; y = g.top ? TOP + 60 : BOT - 60; }
            const pt = svg.createSVGPoint();
            pt.x = x; pt.y = y;
            const s = pt.matrixTransform(m);
            return { x: s.x, y: s.y };
        },
    };
}

/* pip layouts on a unit die */
const PIPS = {
    1: [[0.5, 0.5]],
    2: [[0.27, 0.27], [0.73, 0.73]],
    3: [[0.27, 0.27], [0.5, 0.5], [0.73, 0.73]],
    4: [[0.27, 0.27], [0.73, 0.27], [0.27, 0.73], [0.73, 0.73]],
    5: [[0.27, 0.27], [0.73, 0.27], [0.5, 0.5], [0.27, 0.73], [0.73, 0.73]],
    6: [[0.27, 0.25], [0.73, 0.25], [0.27, 0.5], [0.73, 0.5], [0.27, 0.75], [0.73, 0.75]],
};
